import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, API_BASE, apiFetch } from "@/lib/api";
import { Plus, Trash, VideoCamera, FileText, UploadSimple, Link, PencilSimple, X, Stack, ArrowCounterClockwise, TrashSimple, CloudArrowUp } from "@phosphor-icons/react";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import BulkUploadModal from "@/components/admin/BulkUploadModal";
import { StorageMeterCard } from "@/components/admin/StorageMeterCard";

export default function LibraryPanel({ canDelete }) {
  const { pillars, labelOf, colorOf, termPlural, term } = usePillars();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [show, setShow] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [filter, setFilter] = useState("all");
  const [viewTrash, setViewTrash] = useState(false);
  const [trash, setTrash] = useState([]);
  const [confirmForeverId, setConfirmForeverId] = useState(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [info, setInfo] = useState("");
  const [bunny, setBunny] = useState(null); // {stage: confirm|running|status, ...}
  const [bunnyBusy, setBunnyBusy] = useState(false);

  const bunnyScan = async () => {
    setBunnyBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/bunny/migrate", { dry_run: true });
      setBunny(data.count > 0 ? { stage: "confirm", count: data.count } : null);
      if (data.count === 0) await bunnyStatus();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBunnyBusy(false); }
  };
  const bunnyRun = async () => {
    setBunnyBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/bunny/migrate", { dry_run: false });
      setBunny({ stage: "running", started: data.started, errors: data.errors?.length || 0 });
      setTimeout(bunnyStatus, 4000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBunnyBusy(false); }
  };
  const bunnyStatus = async () => {
    try {
      const { data } = await api.get("/admin/bunny/migrate/status");
      setBunny({ stage: "status", ...data });
    } catch (e) { setErr(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/lesson-assets");
      setAssets(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadTrash = async () => {
    try {
      const { data } = await api.get("/admin/lesson-assets/trash");
      setTrash(data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); loadTrash(); }, []);

  const onDelete = async (id) => {
    setErr("");
    try { await api.delete(`/admin/lesson-assets/${id}`); await load(); await loadTrash(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const onRestore = async (id) => {
    setErr("");
    try { await api.post(`/admin/lesson-assets/${id}/restore`); await load(); await loadTrash(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const onDeleteForever = async (id) => {
    setErr("");
    try { await api.delete(`/admin/lesson-assets/${id}/permanent`); setConfirmForeverId(null); await loadTrash(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const onEmptyTrash = async () => {
    setErr(""); setConfirmEmpty(false);
    try {
      const { data } = await api.post(`/admin/lesson-assets/trash/empty`);
      await load(); await loadTrash();
      if (data.kept && data.kept.length > 0) {
        setInfo(`Trash emptied. ${data.deleted} deleted. Kept & restored ${data.kept.length} shared lesson(s): ${data.kept.map(k => k.title).join(", ")}.`);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };

  const filtered = filter === "all"
    ? assets
    : filter === "__unassigned__"
      ? assets.filter(a => !a.pillar)
      : assets.filter(a => a.pillar === filter);

  return (
    <>
    <StorageMeterCard />
    <div className="card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold font-display">{viewTrash ? `Trash (${trash.length})` : `Lesson Library (${assets.length})`}</h2>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="toggle-trash-view"
            className={viewTrash ? "btn-primary flex items-center gap-1" : "btn-secondary flex items-center gap-1"}
            onClick={() => setViewTrash(v => !v)}
            title="View deleted lessons you can restore"
          >
            <Trash size={16} weight="bold" /> {viewTrash ? "Back to Library" : `Trash${trash.length ? ` (${trash.length})` : ""}`}
          </button>
          {viewTrash && trash.length > 0 && (
            confirmEmpty ? (
              <div className="flex items-center gap-2 px-2">
                <span className="text-xs text-[#B91C1C] max-w-[260px]">Permanently delete {trash.length} lesson(s)? Shared lessons (2+ courses) are kept &amp; restored. Cannot be undone.</span>
                <button data-testid="empty-trash-confirm" className="btn-secondary text-xs leading-none" style={{ padding: "5px 12px", background: "#B91C1C", color: "#fff", borderColor: "#B91C1C" }} onClick={onEmptyTrash}>Empty Trash</button>
                <button className="btn-ghost text-xs text-[#000000]" onClick={() => setConfirmEmpty(false)}>Cancel</button>
              </div>
            ) : (
              <button data-testid="empty-trash-button" className="btn-secondary flex items-center gap-1 text-[#FF5100]" onClick={() => setConfirmEmpty(true)} title="Permanently delete unused lessons; keep shared ones">
                <TrashSimple size={16} weight="bold" /> Empty Trash
              </button>
            )
          )}
          {!viewTrash && (
            <>
              <button data-testid="bunny-migrate-btn" className="btn-secondary flex items-center gap-1" onClick={bunny ? bunnyStatus : bunnyScan} disabled={bunnyBusy} title="Move lesson videos to Bunny Stream / check migration status">
                <CloudArrowUp size={16} weight="bold" /> {bunnyBusy ? "Working…" : "Bunny migration"}
              </button>
              <button data-testid="open-bulk-upload-button" className="btn-secondary flex items-center gap-1" onClick={() => setShowBulk(true)}>
                <Stack size={16} weight="bold" /> Bulk upload
              </button>
              <button data-testid="open-create-asset-button" className="btn-primary flex items-center gap-1" onClick={() => setShow(true)}>
                <Plus size={16} weight="bold" /> Add lesson asset
              </button>
            </>
          )}
        </div>
      </div>
      {bunny && (
        <div className="mb-3 text-sm px-3 py-2 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E3A8A] flex items-center justify-between flex-wrap gap-2" data-testid="bunny-migrate-banner">
          {bunny.stage === "confirm" && (
            <>
              <span><strong>{bunny.count}</strong> video{bunny.count === 1 ? "" : "s"} still on the built-in store. Move them to Bunny Stream? (Lessons keep working during the move.)</span>
              <span className="flex gap-2">
                <button data-testid="bunny-migrate-confirm" className="btn-primary text-xs" style={{ padding: "5px 12px" }} onClick={bunnyRun} disabled={bunnyBusy}>{bunnyBusy ? "Starting…" : "Start migration"}</button>
                <button className="btn-ghost text-xs" onClick={() => setBunny(null)}>Cancel</button>
              </span>
            </>
          )}
          {bunny.stage === "running" && (
            <span>✓ Migration started for <strong>{bunny.started}</strong> videos{bunny.errors ? ` (${bunny.errors} failed to start)` : ""} — Bunny is downloading and preparing them now. Checking status…</span>
          )}
          {bunny.stage === "status" && (
            <>
              <span><strong>{bunny.ready}</strong> of <strong>{bunny.total}</strong> Bunny videos ready · {bunny.processing} preparing{bunny.failed ? ` · ${bunny.failed} FAILED` : ""}</span>
              <span className="flex gap-2">
                <button className="btn-secondary text-xs" style={{ padding: "5px 12px" }} onClick={bunnyStatus} disabled={bunnyBusy}>Refresh</button>
                <button className="btn-ghost text-xs" onClick={() => setBunny(null)}>Dismiss</button>
              </span>
            </>
          )}
        </div>
      )}
      <p className="text-sm text-[#000000] mb-3">
        {viewTrash
          ? "Deleted lessons live here. Restore one to put it back where it was, or Empty Trash to permanently delete them — shared lessons (used in 2+ courses) are kept and restored automatically."
          : "Upload a video file or paste a URL, or add a text lesson. Assets here can be assigned to any course."}
      </p>
      {info && (
        <div className="mb-3 text-sm px-3 py-2 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] flex items-center justify-between" data-testid="library-info">
          <span>{info}</span>
          <button className="text-[#065F46] underline text-xs" onClick={() => setInfo("")}>Dismiss</button>
        </div>
      )}
      {viewTrash ? (
        <>
          {err && <div className="text-[#FF5100] mb-3 text-sm">{err}</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            {trash.map(a => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-2xl border border-dashed border-[#C4C8CE] bg-[#DCDEE2] flex flex-col gap-2 opacity-90"
                data-testid={`trash-asset-${a.id}`}
              >
                <div className="flex items-center gap-2 text-[#000000]">
                  {a.content_type === "video"
                    ? <VideoCamera size={18} weight="duotone" color="#00E5FF" />
                    : <FileText size={18} weight="duotone" color="#CCFF00" />}
                  <span className="chip text-xs font-semibold" style={pillarChipStyle(colorOf(a.pillar))}>
                    {a.pillar ? labelOf(a.pillar) : "Unassigned"}
                  </span>
                </div>
                {a.thumbnail_url && (
                  <img src={a.thumbnail_url} alt={a.title} className="w-full aspect-video object-cover rounded-lg border border-[#C4C8CE] grayscale" data-testid={`trash-asset-thumb-${a.id}`} />
                )}
                <h4 className="font-bold font-display leading-tight">{a.title}</h4>
                <p className="text-xs text-[#000000]">{a.duration_minutes} min · {a.content_type}</p>
                {(() => {
                  const courseCount = new Set((a.trashed_from || []).map(p => p.course_id)).size;
                  return courseCount >= 2 ? (
                    <span className="text-[11px] text-[#7a8a00] font-semibold flex items-center gap-1" data-testid={`trash-protected-${a.id}`}>
                      <ArrowCounterClockwise size={12} weight="bold" /> Shared in {courseCount} courses — kept safe on Empty Trash
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#000000]" data-testid={`trash-usage-${a.id}`}>
                      {courseCount === 1 ? "Was in 1 course" : "Not used in any course"}
                    </span>
                  );
                })()}
                <div className="flex gap-2 mt-1">
                  <button data-testid={`restore-asset-${a.id}`} className="btn-primary text-xs flex items-center gap-1 flex-1 justify-center" onClick={() => onRestore(a.id)}>
                    <ArrowCounterClockwise size={14} weight="bold" /> Restore
                  </button>
                  {canDelete && (
                    confirmForeverId === a.id ? (
                      <div className="flex gap-1 items-center">
                        <button data-testid={`delete-forever-confirm-${a.id}`} className="btn-ghost text-xs font-bold text-[#B91C1C]" onClick={() => onDeleteForever(a.id)} title="Confirm permanent delete">Confirm</button>
                        <button className="btn-ghost text-xs text-[#000000]" onClick={() => setConfirmForeverId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button data-testid={`delete-forever-asset-${a.id}`} className="btn-ghost text-xs flex items-center gap-1 text-[#FF5100]" onClick={() => setConfirmForeverId(a.id)} title="Delete permanently">
                        <TrashSimple size={14} weight="bold" /> Delete forever
                      </button>
                    )
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          {trash.length === 0 && (
            <div className="text-[#000000] text-sm p-6 text-center" data-testid="trash-empty">Trash is empty — nothing to restore.</div>
          )}
        </>
      ) : (
      <>
      <div className="flex gap-1.5 mb-4 overflow-x-auto">
        {[{slug: "all", name: `All ${termPlural.toLowerCase()}`}, ...pillars, {slug: "__unassigned__", name: "Unassigned"}].map(p => (
          <button
            key={p.slug}
            onClick={() => setFilter(p.slug)}
            data-testid={`library-filter-${p.slug}`}
            className="px-3 py-1.5 rounded-full text-xs font-display font-semibold whitespace-nowrap transition-all"
            style={{
              background: filter === p.slug ? "#CCFF00" : "#DCDEE2",
              color: filter === p.slug ? "#DCDEE2" : "#52525B",
              border: "1px solid " + (filter === p.slug ? "#CCFF00" : "#C4C8CE"),
              cursor: "pointer",
            }}
          >{p.name}</button>
        ))}
      </div>

      {err && <div className="text-[#FF5100] mb-3 text-sm">{err}</div>}
      {loading && <div className="text-[#000000]">Loading...</div>}

      {show && <CreateAssetForm onClose={() => setShow(false)} onCreated={load} />}
      {showBulk && <BulkUploadModal onClose={() => setShowBulk(false)} onDone={load} />}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
        {filtered.map(a => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-2xl border border-[#C4C8CE] bg-[#DCDEE2] flex flex-col gap-2"
            data-testid={`library-asset-${a.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-[#000000]">
                {a.content_type === "video"
                  ? <VideoCamera size={18} weight="duotone" color="#00E5FF" />
                  : <FileText size={18} weight="duotone" color="#CCFF00" />}
                <span className="chip text-xs font-semibold" style={pillarChipStyle(colorOf(a.pillar))}>
                  {a.pillar ? labelOf(a.pillar) : "Unassigned"}
                </span>
                {!a.pillar && pillars.length > 0 && (
                  <select
                    data-testid={`reassign-asset-${a.id}`}
                    className="text-xs bg-[#DCDEE2] border border-[#C4C8CE] rounded px-1.5 py-0.5 text-[#000000]"
                    value=""
                    onChange={async (e) => {
                      const slug = e.target.value;
                      if (!slug) return;
                      try {
                        await api.patch(`/admin/lesson-assets/${a.id}`, { pillar: slug });
                        await load();
                      } catch (err) {
                        alert(formatApiError(err));
                      }
                    }}
                  >
                    <option value="">Assign to…</option>
                    {pillars.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                  </select>
                )}
              </div>
              {canDelete && (
                <button data-testid={`delete-asset-${a.id}`} className="btn-ghost p-1" onClick={() => onDelete(a.id)} title="Delete">
                  <Trash size={16} color="#FF5100" />
                </button>
              )}
            </div>
            {a.thumbnail_url && (
              <img src={a.thumbnail_url} alt={a.title} className="w-full aspect-video object-cover rounded-lg border border-[#C4C8CE]" data-testid={`library-asset-thumb-${a.id}`} />
            )}
            <h4 className="font-bold font-display leading-tight">{a.title}</h4>
            <p className="text-xs text-[#000000] line-clamp-2">{a.summary || a.text_body || ""}</p>
            <div className="text-xs text-[#000000] mt-1">
              {a.duration_minutes} min · {a.content_type}
              {a.video_url && a.video_url.includes("/api/files/") && <span className="ml-1 text-[#00E5FF]">· uploaded</span>}
              {a.quiz && (a.quiz.questions || []).length > 0 && (
                <span className="ml-1 text-[#CCFF00]" data-testid={`asset-has-quiz-${a.id}`}>
                  · quiz: {(a.quiz.questions || []).length} Q pool / ask {a.quiz.questions_per_attempt}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {filtered.length === 0 && !loading && (
        <div className="text-[#000000] text-sm p-6 text-center">No assets here yet. Click "Add lesson asset".</div>
      )}
      </>
      )}
    </div>
    </>
  );
}

function CreateAssetForm({ onClose, onCreated }) {
  const { pillars } = usePillars();
  const [mode, setMode] = useState("upload"); // upload | url | text
  const [form, setForm] = useState({
    title: "", pillar: pillars[0]?.slug || "", content_type: "video",
    duration_minutes: 5, video_url: "", text_body: "", summary: "",
  });
  const [file, setFile] = useState(null);
  const [includeQuiz, setIncludeQuiz] = useState(false);
  const [quiz, setQuiz] = useState({
    questions_per_attempt: 3, pass_threshold: 70,
    questions: [{ question: "", options: ["", "", "", ""], correct_index: 0 }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);

  const setQ = (i, key, val) => setQuiz(p => ({ ...p, questions: p.questions.map((q, idx) => idx === i ? { ...q, [key]: val } : q) }));
  const setOpt = (i, oi, val) => setQuiz(p => ({ ...p, questions: p.questions.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) } : q) }));
  const addQ = () => setQuiz(p => ({ ...p, questions: [...p.questions, { question: "", options: ["", "", "", ""], correct_index: 0 }] }));
  const rmQ = (i) => setQuiz(p => ({ ...p, questions: p.questions.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("pillar", form.pillar);
      fd.append("content_type", mode === "text" ? "text" : "video");
      fd.append("duration_minutes", String(form.duration_minutes || 5));
      if (form.summary) fd.append("summary", form.summary);
      if (mode === "upload") {
        if (!file) throw new Error("Please choose a video file to upload");
        fd.append("file", file);
      } else if (mode === "url") {
        if (!form.video_url) throw new Error("Please provide a video URL");
        fd.append("video_url", form.video_url);
      } else {
        if (!form.text_body) throw new Error("Please provide the lesson text");
        fd.append("text_body", form.text_body);
      }
      if (includeQuiz) {
        const cleanQuestions = quiz.questions.map(q => {
          const opts = q.options.filter(o => o.trim());
          if (!q.question.trim() || opts.length < 2) throw new Error("Each question needs text + at least 2 options");
          return { question: q.question.trim(), options: opts, correct_index: Math.min(q.correct_index, opts.length - 1) };
        });
        if (cleanQuestions.length < quiz.questions_per_attempt) throw new Error("Pool size must be >= questions_per_attempt");
        fd.append("quiz_json", JSON.stringify({
          questions: cleanQuestions,
          questions_per_attempt: Number(quiz.questions_per_attempt),
          pass_threshold: Number(quiz.pass_threshold),
        }));
      }
      const res = await apiFetch(`${API_BASE}/admin/lesson-assets`, {
        method: "POST", body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Upload failed");
      }
      onCreated();
      onClose();
    } catch (e) { setErr(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-3"
      data-testid="create-asset-form"
    >
      <div className="flex items-center justify-between">
        <div className="font-bold font-display">New lesson asset</div>
        <button type="button" className="btn-ghost p-1" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="flex gap-1.5">
        {[
          { id: "upload", label: "Upload video", icon: UploadSimple },
          { id: "url", label: "Video URL", icon: Link },
          { id: "text", label: "Text lesson", icon: FileText },
        ].map(({ id, label, icon: Icon }) => (
          <button
            type="button" key={id}
            data-testid={`asset-mode-${id}`}
            onClick={() => setMode(id)}
            className="px-3 py-1.5 rounded-full text-xs font-display font-semibold whitespace-nowrap transition-all flex items-center gap-1"
            style={{
              background: mode === id ? "#CCFF00" : "#FFFFFF",
              color: mode === id ? "#DCDEE2" : "#52525B",
              border: "1px solid " + (mode === id ? "#CCFF00" : "#C4C8CE"),
              cursor: "pointer",
            }}
          ><Icon size={14} weight={mode === id ? "bold" : "duotone"} /> {label}</button>
        ))}
      </div>
      <input data-testid="asset-title-input" className="input-base" placeholder="Lesson title" required value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} />
      <div className="grid grid-cols-2 gap-3">
        <select data-testid="asset-pillar-input" className="input-base" value={form.pillar} onChange={(e)=>setForm({...form, pillar: e.target.value})} required>
          {pillars.length === 0 && <option value="">No pillars yet — create one first</option>}
          {pillars.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select>
        <input data-testid="asset-duration-input" type="number" min="1" className="input-base" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e)=>setForm({...form, duration_minutes: Number(e.target.value)})} />
      </div>

      {mode === "upload" && (
        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef} data-testid="asset-file-input"
            type="file" accept="video/mp4,video/webm,video/quicktime"
            className="text-sm text-[#000000] file:bg-[#CCFF00] file:text-[#000000] file:font-bold file:rounded-full file:px-4 file:py-2 file:border-0 file:mr-3 file:cursor-pointer"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file && <div className="text-xs text-[#000000]">Selected: {file.name} ({(file.size/1024/1024).toFixed(1)} MB)</div>}
        </div>
      )}
      {mode === "url" && (
        <input data-testid="asset-url-input" className="input-base" placeholder="https://...mp4 or YouTube/Vimeo embed URL" value={form.video_url} onChange={(e)=>setForm({...form, video_url: e.target.value})} />
      )}
      {mode === "text" && (
        <textarea data-testid="asset-text-input" rows={6} className="input-base" placeholder="Lesson content..." value={form.text_body} onChange={(e)=>setForm({...form, text_body: e.target.value})} />
      )}

      <textarea data-testid="asset-summary-input" rows={2} className="input-base" placeholder="Short summary (optional)" value={form.summary} onChange={(e)=>setForm({...form, summary: e.target.value})} />

      <label className="flex items-center gap-2 text-sm text-[#000000] font-display font-semibold cursor-pointer">
        <input type="checkbox" data-testid="asset-include-quiz" checked={includeQuiz} onChange={(e) => setIncludeQuiz(e.target.checked)} />
        Attach a quiz to this lesson
      </label>

      {includeQuiz && (
        <div className="p-3 rounded-xl bg-[#FFFFFF] border border-[#C4C8CE] flex flex-col gap-3" data-testid="quiz-builder">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs uppercase text-[#000000] flex flex-col gap-1">
              Questions per attempt
              <input data-testid="quiz-ask-count" type="number" min="1" className="input-base" value={quiz.questions_per_attempt} onChange={(e) => setQuiz(p => ({ ...p, questions_per_attempt: Number(e.target.value) }))} />
            </label>
            <label className="text-xs uppercase text-[#000000] flex flex-col gap-1">
              Pass threshold (%)
              <input data-testid="quiz-pass-threshold" type="number" min="0" max="100" className="input-base" value={quiz.pass_threshold} onChange={(e) => setQuiz(p => ({ ...p, pass_threshold: Number(e.target.value) }))} />
            </label>
          </div>
          <div className="text-xs text-[#000000]">Question pool ({quiz.questions.length}) — the app randomly picks {quiz.questions_per_attempt} per attempt.</div>
          <div className="flex flex-col gap-2">
            {quiz.questions.map((q, qi) => (
              <div key={qi} className="p-2 rounded-lg bg-[#DCDEE2] border border-[#C4C8CE]" data-testid={`pool-q-${qi}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase text-[#000000]">Q{qi+1}</div>
                  {quiz.questions.length > 1 && (
                    <button type="button" className="btn-ghost p-1" onClick={() => rmQ(qi)}><Trash size={12} color="#FF5100" /></button>
                  )}
                </div>
                <input className="input-base mb-1.5" placeholder="Question text" required value={q.question} onChange={(e) => setQ(qi, "question", e.target.value)} data-testid={`pool-q-${qi}-text`} />
                <div className="flex flex-col gap-1">
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-1.5">
                      <input type="radio" name={`pc-${qi}`} checked={q.correct_index === oi} onChange={() => setQ(qi, "correct_index", oi)} data-testid={`pool-q-${qi}-correct-${oi}`} />
                      <input className="input-base flex-1" placeholder={`Option ${oi+1}`} value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} data-testid={`pool-q-${qi}-opt-${oi}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary self-start text-xs" onClick={addQ} data-testid="add-pool-question">+ Add question to pool</button>
        </div>
      )}

      {err && <div className="text-[#FF5100] text-sm">{err}</div>}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary" disabled={busy} data-testid="submit-create-asset">{busy ? "Saving..." : "Add to library"}</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </motion.form>
  );
}
